import { Link } from "react-router-dom";
import LegalShell from "@/components/site/LegalShell";
import SEO from "@/components/site/SEO";

export default function RefundPolicy() {
  return (
    <LegalShell
      title="Refund Policy"
      lastUpdated="22 June 2026"
      pageTestId="refund-policy-page"
      seo={
        <SEO
          title="Refund Policy"
          path="/refund-policy"
          description="OneDial Africa Refund Policy — how Scitygate Telecom UK Ltd handles refunds, cancellations and chargebacks for wallet top-ups, calls, airtime and other digital services."
        />
      }
    >
      <p>
        This Refund Policy explains when and how you can request a refund for charges made
        to your <strong>OneDial Africa</strong> account. It forms part of, and should be
        read alongside, our <Link to="/terms-and-conditions" className="underline">Terms &amp; Conditions</Link> and <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
        OneDial Africa is a trading name of <strong>Scitygate Telecom UK Ltd</strong>,
        registered in England &amp; Wales under company number <strong data-testid="refund-company-number">16468736</strong>,
        registered office: 2nd Floor, Halifax House, 93–101 Bridge Street, Manchester M3 2GX, United Kingdom.
      </p>

      <h2>1. Summary</h2>
      <ul>
        <li>Wallet top-ups are <strong>generally non-refundable</strong> once the funds are credited to your OneDial wallet, because they are immediately available as a digital service credit.</li>
        <li>You have a 14-day cancellation right under UK Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013 <em>only if the credit has not been used</em>.</li>
        <li>Refunds for verified service failures, billing errors and duplicate payments are always considered on a case-by-case basis.</li>
        <li>Promo, referral and gift credits are never refundable to cash but remain on your wallet for service use.</li>
      </ul>

      <h2>2. Wallet top-ups</h2>
      <p>
        When you top up your OneDial wallet via Stripe, your balance is credited as
        <strong> closed-loop GBP service credit</strong>. By starting a top-up you expressly
        request that we begin providing the digital service immediately and you acknowledge
        that, in line with Regulation 36 of the Consumer Contracts Regulations 2013, the
        statutory 14-day cancellation right does not apply once the service has started
        (i.e. once you have used any portion of the credit).
      </p>
      <p>
        Where the credit has <strong>not been used at all</strong>, you may request a
        refund within 14 days of the top-up. Refunds are returned to the original
        payment method via Stripe and typically clear in 5–10 working days.
      </p>

      <h2>3. Calls, airtime, eSIM and other services</h2>
      <p>
        Once a call has been connected, an airtime top-up has been delivered, an eSIM
        QR code has been generated, or any equivalent digital service has been
        rendered, the transaction is <strong>final and non-refundable</strong>.
      </p>
      <p>
        Exceptions where we will refund or re-issue:
      </p>
      <ul>
        <li>The call did not connect or dropped within the first 6 seconds (we re-credit your wallet automatically — there is nothing for you to do).</li>
        <li>The destination number or operator rejected the airtime top-up.</li>
        <li>The eSIM activation profile failed to deliver to your device after 24 hours and could not be replaced.</li>
        <li>A clear, evidenced billing error on our side (e.g. you were debited twice for the same call).</li>
      </ul>

      <h2>4. Promo codes, referral bonuses and gift credit</h2>
      <p>
        Credit you receive from a promo code (e.g. <code>WELCOME5</code>), a referral
        bonus (£2 give / £2 get), or a gift sent by another customer is <strong>not
        redeemable for cash</strong> and is not refundable. It remains in your OneDial
        wallet for use on our services and does not expire.
      </p>
      <p>
        If you have purchased a gift for another person and the recipient has not yet
        claimed it, you may cancel the gift and have the amount returned to your
        wallet by opening a support ticket. Once the gift has been claimed by the
        recipient, the credit belongs to them and cannot be reversed.
      </p>

      <h2>5. How to request a refund</h2>
      <p>The fastest way is to open a support ticket from inside your account:</p>
      <ol>
        <li>Sign in at <Link to="/login" className="underline">onedialafrica.com/login</Link>.</li>
        <li>Go to <Link to="/app/support" className="underline">Support</Link> and click <strong>New ticket</strong>.</li>
        <li>Include the transaction reference, the date and the reason for your refund request.</li>
      </ol>
      <p>
        Alternatively, email <a href="mailto:support@onedialafrica.com" className="underline">support@onedialafrica.com</a> from the address registered to your account. We aim to acknowledge every refund request within 1 working day and resolve it within 10 working days.
      </p>

      <h2>6. Chargebacks</h2>
      <p>
        If you believe a charge is incorrect, please contact us before disputing the
        charge with your bank or card issuer. A unilateral chargeback may result in
        your OneDial wallet being frozen pending review, and our team may need to
        submit evidence to Stripe / Visa / Mastercard on your behalf to recover the
        funds. Fraudulent chargebacks may lead to account closure and reporting to
        relevant authorities.
      </p>

      <h2>7. Disputed transactions</h2>
      <p>
        If we cannot resolve your refund request to your satisfaction, you may
        escalate via:
      </p>
      <ul>
        <li>The Financial Ombudsman Service (for the payment processing element handled by Stripe): <a href="https://www.financial-ombudsman.org.uk" target="_blank" rel="noopener noreferrer" className="underline">financial-ombudsman.org.uk</a></li>
        <li>The Communications &amp; Internet Services Adjudication Scheme (CISAS) (for telecom service complaints): <a href="https://www.cedr.com/consumer/cisas/" target="_blank" rel="noopener noreferrer" className="underline">cedr.com/consumer/cisas</a></li>
        <li>Trading Standards via Citizens Advice: <a href="https://www.citizensadvice.org.uk" target="_blank" rel="noopener noreferrer" className="underline">citizensadvice.org.uk</a></li>
      </ul>

      <h2>8. Closure of inactive accounts</h2>
      <p>
        Closed-loop GBP wallet balances do not expire. If you wish to close your
        account and have your remaining balance returned, you may request this via
        the support process above. We may charge a reasonable administration fee for
        the bank transfer (capped at £5) and may require additional identity
        verification under our anti-fraud obligations.
      </p>

      <h2>9. Changes to this policy</h2>
      <p>
        We may update this Refund Policy from time to time. Material changes will be
        notified to customers in-app and by email at least 14 days before they take
        effect. The “Last updated” date at the top reflects the most recent revision.
      </p>

      <h2>10. Contact</h2>
      <p>
        Scitygate Telecom UK Ltd<br />
        2nd Floor, Halifax House, 93–101 Bridge Street, Manchester M3 2GX, United Kingdom<br />
        Registered in England &amp; Wales · Company No. 16468736<br />
        Telephone: <a href="tel:+441619894842" className="underline">+44 161 989 4842</a><br />
        Email: <a href="mailto:support@onedialafrica.com" className="underline">support@onedialafrica.com</a>
      </p>
    </LegalShell>
  );
}
